import React, { FC, useState, useContext, useEffect } from 'react';
import { decode } from 'entities';
// Not installing packages to appease linter
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery, useMutation } from '@apollo/react-hooks';
import { AudienceList } from 'Components/Lists';
import { FormattedDate } from 'Utils/formattedDate';
import { useDispatch } from 'react-redux';
import { toastMessage } from 'Containers/Toast/actions/toast.actions';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import withErrorBoundary from 'HOC/ErrorBoundary';
import {
  audienceListGQL,
  userPrefsByIdGQL,
  userPrefsCreate,
  userPrefsUpdate,
} from './gqlQuery';

interface Props {
  search?: string;
  instId: string;
  accountId: string;
  filters: any;
  page: number;
  size: number;
  listId: string;
  Selector?: any;
  checkedItems?: any;
  setUserInfo?: any;
  onChecked?: (id: string) => void;
  onUnChecked?: (id: string) => void;
  onTotalRecordsChanged?: (n: string) => void;
  onTotalPagesChanged?: (n: number) => void;
  // This will be used to determine if a checkbox has been previously checked while navigating through other pages
  isChecked?: (id: string) => boolean;
  sortUpdate?: (sort: string) => void; // This will update AudienceExplore so it can pass it to the TableHeader for the export
  totalPages?: string | number;
  totalRecords: number;
}

const formatLastActiveDate = (date: string) => {
  /*
    Incoming format:  YYYY-MM-DD

    Outgoing format:  MM/DD/YYYY
  */
  try {
    const formatted = FormattedDate(date);
    return formatted;
  } catch (error) {
    return '-';
  }
};

const ALL_COLUMNS = [
  'name',
  'major',
  'gender',
  'ethnicity',
  'relativeInterestLevel',
  'hasScheduledVisitAllTime',
  'hasAppliedAllTime',
  'visitorType',
  'enroll',
  'lastActive',
  'city',
  'state',
  'country',
  'dateOfBirth',
  'lastDateActiveWithYouvisit',
  'sat',
  'act',
  'gpa',
  'phone',
  'email',
  'currentSchool',
  'postal',
  'levelOfStudy',
  'dateRegistered',
  'locationRegistered',
  'parentFullName',
  'parentEmail',
  'parentPhone',
  'personalizationSuggestions',
  'personalizationPreferences',
];
const DEFAULT_COLUMNS = [
  'name',
  'major',
  'visitorType',
  'enroll',
  'state',
  'country',
  'dateRegistered',
];
const DEFAULT_SORTING_COLUMN = 'dateRegistered';
const DEFAULT_SORTING_DIRECTION = 'desc';

// Need to have this info
const mapColumnsToKeys = (
  column: string,
  direction?: string,
  noUSALocation?: any
) => {
  switch (column) {
    case 'name':
      return `fullName:${direction}`;
    case 'relativeInterestLevel':
      return `relativeInterestLevel:${direction}`;
    case 'visitorType':
      return `highSchoolLevelToday:${direction},visitorType:${direction}`;
    case 'enroll':
      return `enrollYear:${direction},enrollTerm:${direction}`;
    case 'lastActive':
      return `lastDateActiveWithInstitution:${direction}`;
    case 'location':
      return noUSALocation
        ? `country:${direction}`
        : `city:${direction},state:${direction}`;
    case 'dateOfBirth':
      return `birthDate:${direction}`;
    case 'lastDateActiveWithYouvisit':
      return `lastDateActiveWithYouvisit:${direction}`;
    case 'sat':
      return `sat:${direction}`;
    case 'act':
      return `act:${direction}`;
    case 'gpa':
      return `gpa:${direction}`;
    case 'phone':
      return `phone:${direction}`;
    case 'email':
      return `email:${direction}`;
    case 'currentSchool':
      return `currentSchool:${direction}`;
    case 'postal':
      return `postal:${direction}`;
    case 'major':
      return `major:${direction}`;
    case 'gender':
      return `gender:${direction}`;
    case 'ethnicity':
      return `ethnicity:${direction}`;
    case 'hasScheduledVisitAllTime':
      return `hasScheduledVisitAllTime:${direction}`;
    case 'hasAppliedAllTime':
      return `hasAppliedAllTime:${direction}`;
    case 'levelOfStudy':
      return `levelOfStudy:${direction}`;
    case 'dateRegistered':
      return `dateRegistered:${direction}`;
    case 'locationRegistered':
      return `locationName:${direction}`;
    case 'city':
      return `city:${direction}`;
    case 'state':
      return `state:${direction}`;
    case 'country':
      return `country:${direction}`;
    case 'parentFullName':
      return `parentFullName:${direction}`;
    case 'parentEmail':
      return `parentEmail:${direction}`;
    case 'parentPhone':
      return `parentPhone:${direction}`;
    case 'personalizationPreferences':
      return `personalizationPreferences:${direction}`;
    case 'personalizationSuggestions':
      return `personalizationSuggestions:${direction}`;
    default:
      return '';
  }
};

const getLocation = (city: string, state: string, country: string) => {
  if (country && country === 'USA') {
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return '-';
  }
  if (country && country !== 'USA') {
    return country;
  }
  return '-';
};

const AudienceListContainer: FC<Props> = (props: Props) => {
  const [userTableColumns, setUserTableColumns] = useState([]);
  const [userSortingColumn, setUserSortingColumn] = useState('');
  const [userSortingDirection, setUserSortingDirection] = useState('');
  const [prefId, setPrefId] = useState();
  const [audienceList, setAudienceList] = useState([]);
  const userInfo = [];
  const Dispatch = useDispatch();
  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();
  const {
    search,
    instId,
    accountId,
    filters,
    page,
    size,
    listId,
    Selector,
    checkedItems,
    setUserInfo,
    onChecked,
    onUnChecked,
    onTotalRecordsChanged,
    onTotalPagesChanged,
    isChecked,
    sortUpdate,
    totalPages,
    totalRecords,
  } = props;

  const onCompleted = (data: any) => {
    const key = Object.keys(data)[0];
    const id = JSON.parse(data[key].data.id);
    const pref = JSON.parse(decode(data[key].data.value));

    // local state is set ONLY after the mutation is successful
    setUserTableColumns(pref.tableColumns);
    setUserSortingColumn(pref.sortingColumn);
    setUserSortingDirection(pref.sortingDirection);
    setPrefId(id);
    if (sortUpdate) {
      sortUpdate(`${pref.sortingColumn}:${pref.sortingDirection}`);
    }
  };

  const [createUserPref] = useMutation(userPrefsCreate, {
    onCompleted,
  });
  const [updateUserPref] = useMutation(userPrefsUpdate, {
    onCompleted,
  });

  const { loading: userPrefLoading } = useQuery(userPrefsByIdGQL, {
    variables: { instId, accountId },
    fetchPolicy: 'cache-and-network',
    onCompleted: (userPrefAllData) => {
      const userPrefObj =
        userPrefAllData &&
        userPrefAllData.userPrefs &&
        userPrefAllData.userPrefs
          .filter((pref: any) => pref.category === 'column_config')
          .filter((pref: any) => pref.name === listId)[0];
      const userPrefObjValue = userPrefObj
        ? JSON.parse(decode(userPrefObj.value))
        : {}; // Add an empty obj in case the userPrefAllData is null or undefined
      const tableColumns = userPrefObjValue.tableColumns || DEFAULT_COLUMNS;
      const sortingColumn =
        userPrefObjValue.sortingColumn || DEFAULT_SORTING_COLUMN;
      const sortingDirection =
        userPrefObjValue.sortingDirection || DEFAULT_SORTING_DIRECTION;
      setUserTableColumns(tableColumns);

      setPrefId((userPrefObj && userPrefObj.id) || null);
      setUserSortingColumn(sortingColumn);
      setUserSortingDirection(sortingDirection);
      if (sortUpdate) {
        sortUpdate(`${sortingColumn}:${sortingDirection}`);
      }
    },
  });

  function setUserPreferences(
    columns: string[],
    sortingColumn: string,
    sortingDirection: string
  ) {
    const variables: any = {
      instId,
      accountId,
      name: listId,
      category: 'column_config',
      value: JSON.stringify({
        tableColumns: columns,
        sortingColumn,
        sortingDirection,
      }),
    };

    if (!prefId) {
      createUserPref({ variables });
    } else {
      variables.id = prefId;
      updateUserPref({ variables });
    }
  }

  const { loading } = useQuery(audienceListGQL, {
    variables: {
      instId,
      filtersv2: filters.filtersv2,
      sort: mapColumnsToKeys(
        userSortingColumn,
        userSortingDirection,
        Selector.filter.demographics.geography.usa.selection === 'None'
      ),
      page,
      size,
      search,
      listMemberShip: filters.listMembership,
    },
    onCompleted: (data) => {
      const list =
        data && data.audiences && data.audiences.audienceList
          ? data.audiences.audienceList.map((entry: any) => {
              const updatedEntries = { ...entry };

              Object.keys(updatedEntries).forEach((key) => {
                if (
                  updatedEntries[key] === null ||
                  updatedEntries[key] === '' ||
                  updatedEntries[key] === undefined
                )
                  updatedEntries[key] = '-';
              });
              userInfo.push({
                userKey: entry.userKeyConsolidated,
                userId: entry.id,
              });
              return {
                ...updatedEntries,
                name: `${entry.fullName}`,
                lastActive: formatLastActiveDate(entry.lastActive),
                lastDateActiveWithYouvisit: formatLastActiveDate(
                  entry.lastDateActiveWithYouvisit
                ),
                dateRegistered: formatLastActiveDate(entry.dateRegistered),
                locationRegistered: entry.locationName,
                location: getLocation(entry.city, entry.state, entry.country),
                enroll: entry.enrollTermYear,
              };
            })
          : [];

      if (data && data.audiences && onTotalRecordsChanged) {
        onTotalRecordsChanged(data.audiences.totalRecords);
      }
      if (data && data.audiences && onTotalPagesChanged) {
        onTotalPagesChanged(data.audiences.totalPages);
      }
      setAudienceList(list);
      setUserInfo(userInfo);
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (
      totalRecords &&
      totalRecords > 0 &&
      userTableColumns &&
      userTableColumns.length > 0
    )
      // Combined Analytics event tracking
      track('audiences_explore_page_viewed', {
        initiated_by: 'Default',
        audience_count: totalRecords,
        column_count: userTableColumns.length,
      });
  }, [listId, totalRecords, userTableColumns]);

  return (
    <AudienceList
      checkedItems={checkedItems}
      onChecked={onChecked}
      onUnChecked={onUnChecked}
      pageCount={10}
      loading={
        loading ||
        userPrefLoading ||
        !userTableColumns ||
        !userSortingDirection ||
        !userSortingColumn
      }
      sort={{
        activeColumn: {
          name: userSortingColumn,
          direction: userSortingDirection,
        },
        events: {
          onClickSortHandler: (column: string) => {
            let newSortingDirection;
            if (column === userSortingColumn) {
              newSortingDirection =
                userSortingDirection === 'desc' ? 'asc' : 'desc';
            } else {
              newSortingDirection = 'desc';
            }
            setUserPreferences(userTableColumns, column, newSortingDirection);
          },
        },
      }}
      tableColumns={userTableColumns}
      sortingColumn={userSortingColumn}
      sortingDirection={userSortingDirection}
      allColumns={ALL_COLUMNS}
      defaultColumns={DEFAULT_COLUMNS}
      audienceData={{ list: audienceList }}
      isChecked={isChecked || null}
      changeCols={(cols: any, sortingColumn: any, sortingDirection: any) => {
        setUserPreferences(cols, sortingColumn, sortingDirection);

        // Combined Analytics event tracking
        track('column_configured', {
          column_list: cols.join(','),
          screen: View(location.pathname),
        });

        Dispatch(
          toastMessage({
            message: 'Changes are applied',
            position: 'bottom',
          })
        );
      }}
      currentPage={page}
      totalPageCount={totalPages}
    />
  );
};

AudienceListContainer.defaultProps = {
  search: '',
  Selector: {},
  checkedItems: {},
  onChecked: () => {},
  setUserInfo: () => {},
  onUnChecked: () => {},
  onTotalRecordsChanged: () => {},
  onTotalPagesChanged: () => {},
  isChecked: () => {
    return true;
  },
  sortUpdate: () => {},
  totalPages: 0,
};

export default withErrorBoundary(AudienceListContainer);
